// src/stores/auth.ts
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import TokenManager from '@/utils/token'
import type { MqttConnector, UserInfo } from '@/types/auth'
import { refreshTokenRequest, userInfo as fetchUserInfoApi } from '@/api/auth/auth'
import { isSameConnector } from '@/utils/mqtt/connector'
import mqttClient from '@/utils/mqtt/client'
import router from '@/router'
import { ElMessage } from 'element-plus'
import { connectMqttWithUser, disconnectMqttService } from '@/utils/mqtt/service'
import { useSysMessageStore } from '@/stores/sysMessage'

// 修改1：扩展对话框配置类型，支持多个弹窗
export interface AuthModalState {
  // 单个弹窗模式（如果你用单个弹窗切换模式）
  loginDialog: {
    visible: boolean
    mode: 'login' | 'register' | 'reset-password'
    title?: string
    redirectAfterLogin?: string
    customData?: Record<string, any>
  }
  // 或者分开多个弹窗（如果你用多个独立弹窗）
  showLogin: boolean
  showRegister: boolean
  showForgotPassword: boolean
  hasWechat: boolean
}

export const useAuthStore = defineStore('auth', () => {
  // State - 从TokenManager同步状态
  const token = ref<string | null>(TokenManager.getToken())
  const userInfo = ref<UserInfo | null>(TokenManager.getUserInfo())
  const tokenRefreshPromise = ref<Promise<boolean> | null>(null)

  // 修改2：使用新的弹窗状态结构
  const authModalState = ref<AuthModalState>({
    // 单个弹窗方式
    loginDialog: {
      visible: false,
      mode: 'login'
    },
    // 多个独立弹窗方式（选择一种）
    showLogin: false,
    showRegister: false,
    showForgotPassword: false,
    hasWechat: false
  })

  // Getters
  const isAuthenticated = computed(() => {
    const currentToken = token.value
    return !!currentToken && !TokenManager.isTokenExpired()
  })

  // 修改3：更新getters以匹配新结构
  const isLoginDialogVisible = computed(() => authModalState.value.showLogin)
  const isRegisterDialogVisible = computed(() => authModalState.value.showRegister)
  const isForgotPasswordDialogVisible = computed(() => authModalState.value.showForgotPassword)

  // 如果保留单个弹窗模式
  const loginDialogMode = computed(() => authModalState.value.loginDialog.mode)
  const loginDialogTitle = computed(() => {
    const titles = {
      'login': '登录',
      'register': '注册',
      'reset-password': '重置密码'
    }
    return authModalState.value.loginDialog.title || titles[authModalState.value.loginDialog.mode]
  })

  const currentUser = computed(() => userInfo.value)
  const userId = computed(() => userInfo.value?.uid)
  const userName = computed(() => userInfo.value?.name)
  const userAvatar = computed(() => userInfo.value?.avatar)

  // 检查token是否需要刷新
  const shouldRefreshToken = computed(() => {
    return TokenManager.getTokenTimeLeft() < 10 * 60 * 1000 // 10分钟
  })

  // Actions
  const updateAuthData = (data: {
    accessToken: string
    refreshToken: string
    expiresIn: number
    deviceId?: string
    userInfo: UserInfo
    connector?: MqttConnector
  }) => {
    // 保存到TokenManager
    TokenManager.saveLoginData({
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      expiresIn: data.expiresIn,
      deviceId: data.deviceId,
      connector: data.connector
    })

    // 更新store状态
    token.value = data.accessToken
    if (data.userInfo) {
      userInfo.value = data.userInfo
      TokenManager.updateUserInfo(data.userInfo)
    }

    if (data.connector) {
      applyConnector(data.connector, true)
    } else if (data.userInfo?.connector) {
      applyConnector(data.userInfo.connector, true)
    }
  }

  const applyConnector = (connector: MqttConnector, forceReconnect = false) => {
    const previous = TokenManager.getConnector()
    const changed = !isSameConnector(previous, connector)
    TokenManager.saveConnector(connector)

    if (forceReconnect || changed || !mqttClient.isConnected()) {
      void connectMqtt(connector)
    }
  }

  const connectMqtt = async (connector?: MqttConnector) => {
    const config = connector ?? TokenManager.getConnector()
    const uid = userInfo.value?.uid ?? TokenManager.getUserInfo()?.uid
    if (!config || !uid) return

    try {
      await connectMqttWithUser(uid, config)
    } catch (error) {
      console.error('MQTT 连接失败:', error)
    }
  }

  const disconnectMqtt = () => {
    disconnectMqttService()
  }

  /** 同步用户信息，并在返回 connector 时更新 IM 连接 */
  const syncUserProfile = (info: UserInfo) => {
    const merged: UserInfo = {
      ...(userInfo.value ?? {}),
      ...info
    }
    userInfo.value = merged
    TokenManager.updateUserInfo(merged)

    if (info.connector) {
      applyConnector(info.connector)
    }
  }

  /** 拉取 /api/user/info 并同步用户信息与 IM 连接 */
  const fetchAndSyncUserInfo = async (): Promise<UserInfo | null> => {
    if (!isAuthenticated.value) return null

    try {
      const info = await fetchUserInfoApi()
      syncUserProfile(info)
      return info
    } catch (error) {
      console.warn('获取用户信息失败:', error)
      return null
    }
  }

  /** 应用启动时，若已登录则拉取最新用户信息并恢复 MQTT 连接 */
  const initMqttConnection = () => {
    if (!isAuthenticated.value) return
    void fetchAndSyncUserInfo().then(info => {
      if (!info?.connector) {
        void connectMqtt()
      }
    })
  }


  // 刷新token（核心功能）
  const refreshToken = async (): Promise<boolean> => {
    // 防止并发刷新
    if (tokenRefreshPromise.value) {
      return await tokenRefreshPromise.value
    }

    const currentRefreshToken = TokenManager.getRefreshToken()
    if (!currentRefreshToken) {
      console.warn('没有refreshToken，无法刷新')
      return false
    }

    // 创建刷新Promise
    const refreshPromise = (async (): Promise<boolean> => {
      try {
        // 调用刷新token的API（需要你实现refreshTokenApi）
        const response = await refreshTokenRequest(currentRefreshToken)

        // 保存新的token
        TokenManager.saveLoginData({
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
          expiresIn: response.expiresIn
        })

        // 更新store状态
        token.value = response.accessToken
        console.log('Token刷新成功')
        return true
      } catch (error) {
        console.error('刷新token失败:', error)

        // 刷新失败，清除认证信息
        clearAuth()

        // 如果在非登录页，提示并跳转
        if (!router.currentRoute.value.path.includes('/zhuli')) {
          ElMessage.error('登录已过期，请重新登录')
          router.push('/zhuli')
        }

        return false
      } finally {
        tokenRefreshPromise.value = null
      }
    })()

    tokenRefreshPromise.value = refreshPromise
    return await refreshPromise
  }
  // 清除所有认证信息
  const clearAuth = () => {
    disconnectMqtt()
    TokenManager.clearToken()
    token.value = null
    userInfo.value = null
    tokenRefreshPromise.value = null
  }

  // 修改4：更新弹窗相关actions（多个独立弹窗版本）
  const openLoginDialog = (hasWechat = false) => {
    authModalState.value = {
      ...authModalState.value,
      showLogin: true,
      showRegister: false,
      showForgotPassword: false,
      hasWechat
    }
  }

  const openRegisterDialog = () => {
    authModalState.value = {
      ...authModalState.value,
      showLogin: true,
      showRegister: true,
      showForgotPassword: false
    }
  }

  const openForgotPasswordDialog = () => {
    authModalState.value = {
      ...authModalState.value,
      showLogin: true,
      showRegister: false,
      showForgotPassword: true
    }
  }

  const closeAllAuthModals = () => {
    authModalState.value.showLogin = false
    authModalState.value.showRegister = false
    authModalState.value.showForgotPassword = false
    authModalState.value.loginDialog.visible = false // 添加这一行
  }

  const closeLoginDialog = () => {
    authModalState.value.showLogin = false
    authModalState.value.loginDialog.visible = false
  }

  const closeRegisterDialog = () => {
    authModalState.value.showRegister = false
  }

  const closeForgotPasswordDialog = () => {
    authModalState.value.showForgotPassword = false
  }

  // 修改5：更新登录成功处理
  const handleLoginSuccess = (data: {
    accessToken: string
    refreshToken: string
    expiresIn: number
    deviceId?: string
    userInfo: UserInfo
    connector?: MqttConnector
  }) => {
    // 调用原有的 updateAuthData
    updateAuthData(data)
    useSysMessageStore().loadHistory(data.userInfo?.uid)

    // 关闭所有弹窗
    closeAllAuthModals()

    // 显示成功消息
    //ElMessage.success(`欢迎回来，${data.userInfo.name || '用户'}！`)

    // 如果有重定向路径，则跳转
    if (authModalState.value.loginDialog.redirectAfterLogin) {
      router.push(authModalState.value.loginDialog.redirectAfterLogin)
    }

    return true
  }

  // 注册成功处理
  const handleRegisterSuccess = (data: {
    accessToken: string
    refreshToken: string
    expiresIn: number
    deviceId?: string
    userInfo: UserInfo
    connector?: MqttConnector
  }) => {
    // 调用原有的 updateAuthData
    updateAuthData(data)
    useSysMessageStore().loadHistory(data.userInfo?.uid)

    // 关闭所有弹窗
    closeAllAuthModals()

    // 显示成功消息
    ElMessage.success('注册成功！')

    return true
  }

  // 修改6：添加切换方法
  const switchToLoginFromRegister = () => {
    openLoginDialog(authModalState.value.hasWechat)
  }

  const switchToLoginFromForgotPassword = () => {
    openLoginDialog(authModalState.value.hasWechat)
  }

  const updateUserInfo = (info: UserInfo) => {
    syncUserProfile(info)
  }

  return {
    // State
    token,
    userInfo,
    authModalState, // 修改：重命名为authModalState

    // Getters
    isAuthenticated,
    currentUser,
    userId,
    userName,
    userAvatar,
    shouldRefreshToken,

    // 弹窗状态getters
    isLoginDialogVisible,
    isRegisterDialogVisible,
    isForgotPasswordDialogVisible,
    loginDialogMode,
    loginDialogTitle,

    // Actions
    updateAuthData,
    refreshToken,
    clearAuth,
    updateUserInfo,
    syncUserProfile,
    fetchAndSyncUserInfo,

    // 弹窗actions
    openLoginDialog,
    closeLoginDialog,
    openRegisterDialog,
    closeRegisterDialog,
    openForgotPasswordDialog,
    closeForgotPasswordDialog,
    closeAllAuthModals,
    switchToLoginFromRegister,
    switchToLoginFromForgotPassword,
    handleLoginSuccess,
    handleRegisterSuccess,
    connectMqtt,
    disconnectMqtt,
    initMqttConnection
  }
})